'use client'

import { useId, useState } from 'react'

import type { ReactNode } from 'react'

import { IconChevDown } from '@/app/components/atoms/Icons'
import { cx } from '@/app/lib/utils'

import styles from './PerformancePromoBlock.module.scss'

interface PromotionsDisclosureProps {
  /** Cuántas promociones quedan plegadas. El botón lo muestra literalmente. */
  readonly hiddenCount: number
  /** Las promociones plegadas, ya renderizadas por el servidor. */
  readonly children: ReactNode
}

/**
 * Desplegable «Todas las promociones (N)» — la ÚNICA hoja cliente de esta
 * feature.
 *
 * El contenido plegado llega ya renderizado desde el servidor como `children`:
 * este componente solo decide si se muestra. Así el HTML de las promociones
 * ocultas se genera en servidor (indexable, sin coste de hidratación) y aquí
 * únicamente vive el estado de apertura.
 *
 * Accesibilidad: `<button aria-expanded aria-controls>` apuntando al contenedor
 * que gobierna (WCAG 2.1 AA, 4.1.2). El panel permanece **montado** siempre y
 * se oculta con el atributo nativo `hidden` en vez de desmontarse: así
 * `aria-controls` nunca referencia un id inexistente en el DOM (fallo que sí
 * detectan axe-core y VoiceOver aunque no jsx-a11y), y `hidden` ya sacan sus
 * descendientes del árbol de accesibilidad y del orden de tabulación
 * mientras está plegado — sin necesidad de desmontar nada.
 */
export function PromotionsDisclosure({ hiddenCount, children }: PromotionsDisclosureProps) {
  const [expanded, setExpanded] = useState(false)
  const panelId = useId()

  if (hiddenCount <= 0) return null

  return (
    <>
      <button
        type="button"
        className={styles.allPromos}
        aria-expanded={expanded}
        aria-controls={panelId}
        onClick={() => setExpanded((value) => !value)}
      >
        {expanded ? 'Ver menos promociones' : `Todas las promociones (${hiddenCount})`}
        <IconChevDown size={16} className={cx(expanded && styles.chevronUp)} />
      </button>

      <div id={panelId} className={styles.promoGrid} hidden={!expanded}>
        {children}
      </div>
    </>
  )
}
